import { useEffect, useEffectEvent, useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { reportError } from "../../shared/api/errors.ts";
import { useAppEnvironment } from "../../shared/environment.tsx";
import { parseScannedQr } from "../../shared/qr/claimQr.ts";
import { CameraError, type CameraFacing, type CameraFailure, type QrScan } from "../../shared/qr/qrSource.ts";
import { ArtEva } from "../../shared/ui/ArtEva/ArtEva.tsx";
import { BottomSheet, useCloseSheet } from "../../shared/ui/BottomSheet/BottomSheet.tsx";
import { Button } from "../../shared/ui/Button/Button.tsx";
import { FlashIcon, SwitchCameraIcon } from "../../shared/ui/icons.tsx";
import { Input } from "../../shared/ui/Input/Input.tsx";
import styles from "./ScanSheet.module.css";

const WIFI_HINT =
  "Это QR для подключения E-va к Wi-Fi. Подключитесь к сети EVA-SETUP, введите данные домашней сети, затем отсканируйте новый QR на экране E-va";

/** `checking` — узнаём, разрешена ли камера; `permission` — 06.02; `denied` — 06.03; `manual` — «Не удаётся отсканировать?». */
type View = "checking" | "permission" | "scanning" | "denied" | "unavailable" | "manual";

/**
 * Шторка «Подключение устройства» (06.01–06.03): встроенный сканер QR привязки.
 * Распознанная ссылка или голый claim token ведут на подтверждение привязки `/claim?token=…`.
 */
export function ScanSheet() {
  const { qrSource } = useAppEnvironment();
  const navigate = useNavigate();
  const close = useCloseSheet("/feed");
  const [view, setView] = useState<View>("checking");

  useEffect(() => {
    let cancelled = false;
    qrSource.permission().then((permission) => {
      if (cancelled) return;
      setView((current) => {
        if (current !== "checking") return current;
        if (permission === "granted") return "scanning";
        return permission === "denied" ? "denied" : "permission";
      });
    }, reportError);
    return () => {
      cancelled = true;
    };
  }, [qrSource]);


  function claim(token: string) {
    // replace: «назад» с подтверждения ведёт туда, откуда открыли сканер, а не снова к камере.
    void navigate(`/claim?token=${encodeURIComponent(token)}`, { replace: true });
  }

  return (
    <BottomSheet open onClose={close} title="Подключение устройства">
      {view === "checking" && <div className={styles.checking} aria-busy="true" />}
      {view === "permission" && (
        <CameraNotice
          heading="Разрешите доступ к камере"
          description="Камера нужна, чтобы отсканировать QR-код на устройстве и добавить его в приложение."
          action="Разрешить камеру"
          onAction={() => setView("scanning")}
          onBack={close}
        />
      )}
      {view === "scanning" && (
        <Scanner onClaim={claim} onCameraError={(reason) => setView(reason)} onManual={() => setView("manual")} />
      )}
      {view === "denied" && (
        <CameraNotice
          heading="Нет доступа к камере"
          description="Разрешите доступ к камере в настройках браузера или отсканируйте QR-код системной камерой телефона. Ещё можно вставить ссылку из QR-кода."
          action="Вставить ссылку"
          onAction={() => setView("manual")}
          onBack={close}
        />
      )}
      {view === "unavailable" && (
        <CameraNotice
          heading="Камера недоступна"
          description="Отсканируйте QR-код на экране E-va системной камерой телефона или вставьте ссылку из QR-кода."
          action="Вставить ссылку"
          onAction={() => setView("manual")}
          onBack={close}
        />
      )}
      {view === "manual" && <ManualClaim onClaim={claim} onScan={() => setView("scanning")} />}
    </BottomSheet>
  );
}

type ScanHint = "wifi" | "unknown" | null;

/** 06.01 / DEVICE / QR Scan (Figma 353:6705): непрерывное распознавание, без кнопки «затвор». */
function Scanner({
  onClaim,
  onCameraError,
  onManual,
}: {
  onClaim: (token: string) => void;
  onCameraError: (reason: CameraFailure) => void;
  onManual: () => void;
}) {
  const { qrSource } = useAppEnvironment();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [facing, setFacing] = useState<CameraFacing>("environment");
  const [scan, setScan] = useState<QrScan | null>(null);
  const [torchOn, setTorchOn] = useState(false);
  const [hint, setHint] = useState<ScanHint>(null);

  const handleCode = useEffectEvent((text: string, stop: () => void) => {
    const scanned = parseScannedQr(text);
    if (scanned.kind === "claim") {
      stop();
      onClaim(scanned.token);
    } else {
      setHint(scanned.kind);
    }
  });
  const handleError = useEffectEvent((error: unknown) => {
    if (error instanceof CameraError) return onCameraError(error.reason);
    reportError(error);
    onCameraError("unavailable");
  });

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    let stopped = false;
    let running: QrScan | null = null;
    const stop = () => {
      stopped = true;
      running?.stop();
    };
    qrSource
      .start({
        video,
        facing,
        onCode: (text) => {
          // Распознавание может прислать ещё кадр, пока камера выключается.
          if (!stopped) handleCode(text, stop);
        },
      })
      .then(
        (started) => {
          if (stopped) return started.stop();
          running = started;
          setScan(started);
          setTorchOn(false);
        },
        (error: unknown) => {
          if (!stopped) handleError(error);
        },
      );
    return () => {
      stop();
      setScan(null);
    };
  }, [qrSource, facing]);

  async function toggleTorch() {
    if (!scan) return;
    const next = !torchOn;
    try {
      await scan.setTorch(next);
      setTorchOn(next);
    } catch (error) {
      reportError(error);
    }
  }

  return (
    <div className={styles.content}>
      <div className={styles.media}>
        <video ref={videoRef} className={styles.video} autoPlay muted playsInline aria-label="Камера" />
        <span className={styles.mascot} aria-hidden="true">
          {/* Art‑Eva в видоискателе 06.01 — 120.455×100 (Figma 933:3586). */}
          <ArtEva width={120.455} />
        </span>
      </div>
      {hint ? (
        <p className={hint === "wifi" ? styles.hint : `${styles.hint} ${styles.notEva}`} role="alert">
          {hint === "wifi" ? WIFI_HINT : "Это не QR E-va. Наведите камеру на QR-код на экране E-va"}
        </p>
      ) : (
        <p className={styles.description}>
          Отсканируйте QR-код, расположенный на устройстве, чтобы добавить его в приложение
        </p>
      )}
      {(scan?.torch || scan?.canSwitchCamera) && (
        <div className={styles.controls}>
          {scan?.torch && (
            <button
              type="button"
              className={`${styles.control} ${styles.torch}`}
              aria-label="Фонарик"
              aria-pressed={torchOn}
              onClick={() => void toggleTorch()}
            >
              <FlashIcon />
            </button>
          )}
          {scan?.canSwitchCamera && (
            <button
              type="button"
              className={`${styles.control} ${styles.switch}`}
              aria-label="Сменить камеру"
              onClick={() => setFacing((current) => (current === "environment" ? "user" : "environment"))}
            >
              <SwitchCameraIcon />
            </button>
          )}
        </div>
      )}
      <button type="button" className={styles.textButton} onClick={onManual}>
        Не удаётся отсканировать?
      </button>
    </div>
  );
}

/** 06.02 / Camera Permission (Figma 953:12871) и 06.03 / Camera Denied (Figma 953:12840). */
function CameraNotice({
  heading,
  description,
  action,
  onAction,
  onBack,
}: {
  heading: string;
  description: string;
  action: string;
  onAction: () => void;
  onBack: () => void;
}) {
  return (
    <div className={styles.notice}>
      <ArtEva width={212} className={styles.noticeArt} />
      <div className={styles.text}>
        <h3 className={styles.heading}>{heading}</h3>
        <p className={styles.description}>{description}</p>
      </div>
      <div className={styles.actions}>
        <Button onClick={onAction}>{action}</Button>
        <Button variant="secondary" onClick={onBack}>
          Назад
        </Button>
      </div>
    </div>
  );
}

/**
 * «Не удаётся отсканировать?»: как обойтись без встроенного сканера и поле для ссылки из QR.
 * Отдельного макета нет, каркас — как у 06.02.
 */
function ManualClaim({ onClaim, onScan }: { onClaim: (token: string) => void; onScan: () => void }) {
  const [link, setLink] = useState("");
  const [error, setError] = useState<string>();

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!link.trim()) return setError("Вставьте ссылку");
    const scanned = parseScannedQr(link);
    if (scanned.kind === "claim") return onClaim(scanned.token);
    setError(scanned.kind === "wifi" ? WIFI_HINT : "Это не ссылка из QR-кода E-va");
  }

  return (
    <form className={styles.manual} onSubmit={submit} noValidate>
      <div className={styles.text}>
        <h3 className={styles.heading}>Не удаётся отсканировать?</h3>
      </div>
      <ul className={styles.steps}>
        <li>
          Наведите системную камеру телефона на QR-код на экране E-va и откройте ссылку: приложение сразу предложит
          подключить E-va.
        </li>
        <li>
          На экране E-va QR для Wi-Fi? Сначала настройте Wi-Fi: подключитесь к сети EVA-SETUP, введите данные домашней
          сети, затем отсканируйте новый QR на экране E-va.
        </li>
        <li>Или скопируйте ссылку из QR-кода и вставьте её сюда.</li>
      </ul>
      <Input
        label="Вставить ссылку"
        name="link"
        type="url"
        inputMode="url"
        autoComplete="off"
        placeholder="https://…/claim?token=…"
        value={link}
        onChange={(event) => {
          setLink(event.target.value);
          setError(undefined);
        }}
        error={error}
      />
      <div className={styles.actions}>
        <Button type="submit">Продолжить</Button>
        <Button variant="secondary" onClick={onScan}>
          Сканировать QR-код
        </Button>
      </div>
    </form>
  );
}
